import { inject, Injectable } from "@angular/core";
import { chunk } from "lodash-es";
import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  throwError,
  toArray,
} from "rxjs";
import {
  isOpenSchedule,
  isTakeDown,
  PublishSchedule,
  PublishScheduleAction,
} from "@app/shared/models/publish-schedule.model";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import { ENRICHMENT_HEADER_TRANSPORT } from "../../../services/enrichment-data.service";
import { PriceRowKey } from "./enrichment-list.types";

/** The open schedules of one price on the channel. A price may hold one open
 *  publish AND one open take-down at once. */
export type OpenSchedules = Partial<
  Record<PublishScheduleAction, PublishSchedule>
>;

/** Open schedules by price id. Keyed by price, not by (package, price): the
 *  BFF allows one open schedule per (price, channel, direction), whichever
 *  package the request went through. */
export type OpenScheduleIndex = ReadonlyMap<string, OpenSchedules>;

export const NO_OPEN_SCHEDULES: OpenScheduleIndex = new Map();

export function indexOpenSchedules(
  schedules: readonly PublishSchedule[],
): OpenScheduleIndex {
  const index = new Map<string, OpenSchedules>();
  for (const schedule of schedules) {
    if (!isOpenSchedule(schedule)) continue;
    const direction: PublishScheduleAction = isTakeDown(schedule)
      ? "unpublish"
      : "publish";
    const entry = index.get(schedule.priceId) ?? {};
    entry[direction] ??= schedule;
    index.set(schedule.priceId, entry);
  }
  return index;
}

export function openScheduleOf(
  index: OpenScheduleIndex,
  priceId: string,
  direction: PublishScheduleAction,
): PublishSchedule | undefined {
  return index.get(priceId)?.[direction];
}

const SCHEDULE_READ_CONCURRENCY = 4;

const MAX_PRICE_IDS_PER_SCHEDULE_READ = 100;

const MISSING_ID_ANSWER = "bad request: id is required";

function predatesReadByPrice(error: unknown): boolean {
  const { status, error: body } = (error ?? {}) as {
    status?: number;
    error?: { desc?: string } | null;
  };
  return status === 400 && body?.desc === MISSING_ID_ANSWER;
}

@Injectable({ providedIn: "root" })
export class OpenScheduleLoader {
  private readonly enrichmentService = inject(EnrichmentService);
  private bffPredatesReadByPrice = false;

  load(
    rows: readonly PriceRowKey[],
    channel: string,
  ): Observable<OpenScheduleIndex> {
    if (this.bffPredatesReadByPrice) return this.loadByPackage(rows, channel);
    return this.loadByPrice(rows, channel).pipe(
      catchError(() => {
        this.bffPredatesReadByPrice = true;
        return this.loadByPackage(rows, channel);
      }),
    );
  }

  private loadByPrice(
    rows: readonly PriceRowKey[],
    channel: string,
  ): Observable<OpenScheduleIndex> {
    const priceIds = distinct(rows.map((row) => row.priceId));
    return this.indexReads(
      chunk(priceIds, MAX_PRICE_IDS_PER_SCHEDULE_READ),
      (ids) =>
        this.enrichmentService
          .listOpenPublishSchedules(ids, {
            ...ENRICHMENT_HEADER_TRANSPORT,
            channel,
          })
          .pipe(
            catchError((error: unknown) =>
              predatesReadByPrice(error)
                ? throwError(() => error)
                : of({ data: [] }),
            ),
          ),
    );
  }

  private loadByPackage(
    rows: readonly PriceRowKey[],
    channel: string,
  ): Observable<OpenScheduleIndex> {
    return this.indexReads(distinct(rows.map((row) => row.offeringId)), (id) =>
      this.enrichmentService
        .listPublishSchedules(id, { ...ENRICHMENT_HEADER_TRANSPORT, channel })
        .pipe(catchError(() => of({ data: [] }))),
    );
  }

  private indexReads<K>(
    keys: readonly K[],
    read: (key: K) => Observable<{ data?: PublishSchedule[] | null }>,
  ): Observable<OpenScheduleIndex> {
    if (keys.length === 0) return of(NO_OPEN_SCHEDULES);
    return from(keys).pipe(
      mergeMap(
        (key) => read(key).pipe(map((res) => res.data ?? [])),
        SCHEDULE_READ_CONCURRENCY,
      ),
      toArray(),
      map((lists) => indexOpenSchedules(lists.flat())),
    );
  }
}

function distinct(values: readonly string[]): string[] {
  return [...new Set(values)].filter(Boolean);
}
